import { z } from 'zod';

export const PIMS_SYSTEMS = {
  AVIMARK: 'AVImark',
  CORNERSTONE: 'Cornerstone',
  EZYVET: 'ezyVet',
  IMPROMED: 'ImproMed',
  NEO: 'Neo',
  EVETPRACTICE: 'eVetPractice',
  SHEPHERD: 'Shepherd',
  INSTINCT: 'Instinct',
  INTRAVET: 'IntraVet',
  VETSPIRE: 'Vetspire',
  CLIENTRAX: 'ClienTrax',
  DIGITAIL: 'Digitail',
} as const;

export type PimsSystem = keyof typeof PIMS_SYSTEMS;

const PIMS_SYSTEM_KEYS = Object.keys(PIMS_SYSTEMS) as [PimsSystem, ...PimsSystem[]];

// Keyed by the lowercased, alphanumeric-only form of the raw `integrationSystem` value.
const PIMS_SYSTEM_BY_NORMALIZED_NAME: Record<string, PimsSystem> = {
  avimark: 'AVIMARK',
  cornerstone: 'CORNERSTONE',
  ezyvet: 'EZYVET',
  impromed: 'IMPROMED',
  triplecrown: 'IMPROMED',
  impromedtriplecrown: 'IMPROMED',
  neo: 'NEO',
  evetpractice: 'EVETPRACTICE',
  shepherd: 'SHEPHERD',
  instinct: 'INSTINCT',
  intravet: 'INTRAVET',
  vetspire: 'VETSPIRE',
  clientrax: 'CLIENTRAX',
  digitail: 'DIGITAIL',
};

export const normalizePimsSystem = (raw: string | null | undefined): PimsSystem | null => {
  if (!raw) {
    return null;
  }
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  return Object.prototype.hasOwnProperty.call(PIMS_SYSTEM_BY_NORMALIZED_NAME, key)
    ? PIMS_SYSTEM_BY_NORMALIZED_NAME[key]
    : null;
};

export const promptTemplateAudienceSchema = z
  .object({
    pims: z
      .object({
        mode: z.enum(['include', 'exclude']),
        systems: z.array(z.enum(PIMS_SYSTEM_KEYS)).min(1),
      })
      .strict()
      .optional(),
  })
  .strict()
  .nullable();

export type PromptTemplateAudience = z.infer<typeof promptTemplateAudienceSchema>;

export const templateMatchesClinic = (
  audience: PromptTemplateAudience | undefined,
  { pimsSystem }: { pimsSystem: PimsSystem | null }
): boolean => {
  const pimsRule = audience?.pims;
  if (!pimsRule) {
    return true;
  }
  // A clinic whose PIMS is unknown cannot be shown to satisfy any PIMS rule, include or exclude.
  if (!pimsSystem) {
    return false;
  }
  const isListed = pimsRule.systems.includes(pimsSystem);
  return pimsRule.mode === 'include' ? isListed : !isListed;
};
